import { inject, Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, tap } from 'rxjs';
import { CompanyDetails } from '../../../shared/models/company-details';
import { AssignLeadResponse } from '../models/assign-lead-body';
import { Company } from '../../../shared/interfaces/company';
import { PaginatedResponse } from '../../../shared/interfaces/pagination';
import { LeadStatusCount } from '../../dashboard/models/lead-status-count';
import { LeadStatus } from '../models/lead-status';

@Injectable({
  providedIn: 'root',
})
export class LeadsService {
  private apiUrl = environment.apiBaseUrl;
  private http: HttpClient = inject(HttpClient);

  private leadsSubject = new BehaviorSubject<Company[] | null>(null);
  leads$ = this.leadsSubject.asObservable();

  private attributeChange = new Subject<number>(); // Default value
  attributeChanged = this.attributeChange.asObservable();

  private statusCountRefresh = new Subject<void>();
  statusCountRefresh$ = this.statusCountRefresh.asObservable();

  triggerAttributeChanged(leadId: number) {
    this.attributeChange.next(leadId);
  }

  triggerStatusCountRefresh() {
    this.statusCountRefresh.next();
  }

  getLeads(
    page = 1,
    pageSize = 10,
    assignedToMe = false,
    searchQuery = '',
    leadStatuses = '',
    leadStatusUpdatedOnLT = '',
    updateSubject = true,
    isUpdatePopup = false,
  ): Observable<PaginatedResponse<Company>> {
    const params: Record<string, string> = {
      page: page.toString(),
      page_size: pageSize.toString(),
      assigned_to_me: assignedToMe.toString(),
      search_query: searchQuery,
      is_reminder: isUpdatePopup.toString(),
    };

    let httpParams = new HttpParams({ fromObject: params });

    if (leadStatuses !== '') {
      const statuses = leadStatuses.split(',');
      statuses.forEach((status) => {
        httpParams = httpParams.append('lead_status', status);
      });
    }

    if (leadStatusUpdatedOnLT !== '') {
      httpParams = httpParams.append(
        'lead_status_updated_on_lt',
        leadStatusUpdatedOnLT,
      );
    }

    return this.http
      .get<
        PaginatedResponse<Company>
      >(`${this.apiUrl}/api/lead/`, { params: httpParams })
      .pipe(
        tap((paginatedResponse) => {
          if (updateSubject) {
            this.leadsSubject.next(paginatedResponse.results);
          }
        }),
      );
  }

  getNoStatusLeads(
    page = 1,
    pageSize = 10,
  ): Observable<PaginatedResponse<Company>> {
    const params = new HttpParams({
      fromObject: {
        page: page.toString(),
        page_size: pageSize.toString(),
        assigned_to_me: 'false',
        lead_status: '0',
      },
    });

    return this.http.get<PaginatedResponse<Company>>(
      `${this.apiUrl}/api/lead/`,
      { params },
    );
  }

  getOutdatedStatusLeads(
    page = 1,
    pageSize = 10,
  ): Observable<PaginatedResponse<Company>> {
    const params = new HttpParams({
      fromObject: {
        page: page.toString(),
        page_size: pageSize.toString(),
        assigned_to_me: 'false',
        is_reminder: 'true',
      },
    });

    return this.http.get<PaginatedResponse<Company>>(
      `${this.apiUrl}/api/lead/`,
      { params },
    );
  }

  getLeadDetailsById(
    leadId: number,
    source: string,
    refreshCache: boolean,
  ): Observable<CompanyDetails> {
    let params = new HttpParams();

    if (source) {
      params = params.set('source', source);
    }
    if (source === 'dnb') {
      params = params.set('category', '101');
    }
    params = params.set('nocache', refreshCache);

    return this.http.get<CompanyDetails>(
      `${this.apiUrl}/api/lead/${leadId}/detail`,
      { params },
    );
  }

  getLeadsDetailByIds(leadIds: string): Observable<CompanyDetails[]> {
    return this.http.get<CompanyDetails[]>(
      `${this.apiUrl}/api/leads/${leadIds}/details?category=101`,
    );
  }

  unassignLead(recordUniqueId: string): Observable<unknown> {
    return this.http.delete(`${this.apiUrl}/api/lead/${recordUniqueId}`).pipe(
      tap(() => {
        const currentLeadsList = this.leadsSubject.getValue();
        const updatedLeadsList = currentLeadsList?.filter(
          (lead) => lead.unique_id !== recordUniqueId,
        );
        if (updatedLeadsList) {
          this.leadsSubject.next(updatedLeadsList);
        }
      }),
    );
  }

  assignLead(leads: Company[]): Observable<AssignLeadResponse> {
    const uniqueIds = leads.map((lead) => lead.unique_id);

    const reqBody = {
      reference: {
        type: 'record',
        uniqueIds: uniqueIds,
      },
    };

    return this.http
      .post<AssignLeadResponse>(`${this.apiUrl}/api/lead/`, reqBody)
      .pipe(
        tap((res: AssignLeadResponse) => {
          const currentLeadsList = this.leadsSubject.getValue();
          if (res.success && res.success.length > 0 && currentLeadsList) {
            leads.forEach((lead, index) => {
              lead.leadId = res.success[index]?.id;
              currentLeadsList.push(lead);
            });
          }
          this.leadsSubject.next(currentLeadsList);
        }),
      );
  }

  updateLeads(leads: Company[]): void {
    this.leadsSubject.next(leads);
  }

  getLeadsStatusCounts(): Observable<LeadStatusCount[]> {
    return this.http.get<LeadStatusCount[]>(
      this.apiUrl + '/api/lead/status/count',
    );
  }

  updateLeadStatus(updateLeads: LeadStatus[]): Observable<unknown> {
    return this.http.patch(
      `${this.apiUrl}/api/lead/status/update`,
      updateLeads,
    );
  }
}
